import { FC, useState } from 'react';
import {
  Dialog,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  Grid,
  TextField,
  OutlinedInput,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAbility } from '@casl/react';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { KeyedMutator } from 'swr';
import palette from '../../theme/palette';
import Rk9Api from '../../dataServices/Rk9Api';
import { POST } from '../../constants/requests';
import { AbilityContext } from '../../context/AbilityContext';
import { LEVEL_ERROR, LogError } from '../../dataServices/Logger';
import { User } from '../../types/User';

interface SignUpDialogProps {
  open: boolean;
  close: () => void;
  mutate: KeyedMutator<User[]>;
}

export const SignUpDialog: FC<SignUpDialogProps> = (props) => {
  const { open, close, mutate } = props;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [dogName, setDogName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const ability = useAbility(AbilityContext);

  const resetInputs = () => {
    close();
    setName('');
    setUsername('');
    setPassword('');
    setDogName('');
  };

  const submitSignUp = async () => {
    if (username && password && ability.can('create', 'All')) {
      const newSignUpBody = {
        name: name.trim(),
        role: 'client',
        username: username.trim(),
        password: password.trim(),
        dogName: dogName.trim(),
      };
      await Rk9Api(POST, '/users/sign-up', newSignUpBody)
        .then(() => {
          enqueueSnackbar(
            `Sign up was successfull! Please share the following login info with the new client! username: ${username}, password: ${password}`,
            {
              persist: true,
              variant: 'success',
            },
          );
          resetInputs();
        })
        .catch((error: any) => {
          LogError(LEVEL_ERROR, error, 'User Sign Up');
          enqueueSnackbar('There was a problem signing the client up. Please let someone know!', {
            persist: true,
            variant: 'error',
          });
        });
      await mutate();
    }
  };

  return (
    <Dialog open={open} onClose={resetInputs}>
      <Grid
        container
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
        }}
      >
        <Grid item>
          <Typography
            variant="h3"
            style={{ color: palette.paper.secondary, fontWeight: 700, marginBottom: '20px' }}
          >
            Add Client
          </Typography>
        </Grid>
        <Grid item>
          <TextField
            placeholder="Name"
            required
            value={name}
            size="small"
            fullWidth
            variant="outlined"
            onChange={(e) => setName(e.target.value)}
            style={{ marginBottom: '20px ' }}
          />
        </Grid>
        <Grid item>
          <TextField
            placeholder="Username"
            required
            value={username}
            size="small"
            fullWidth
            variant="outlined"
            onChange={(e) => setUsername(e.target.value)}
            style={{ marginBottom: '20px ' }}
          />
        </Grid>
        <Grid item>
          <OutlinedInput
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            required
            value={password}
            size="small"
            fullWidth
            onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
              setPassword(e.target.value);
            }}
            style={{ marginBottom: '20px ' }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </Grid>
        <Grid item>
          <TextField
            placeholder="Dog's Name"
            required
            value={dogName}
            size="small"
            fullWidth
            variant="outlined"
            onChange={(e) => setDogName(e.target.value)}
            style={{ marginBottom: '20px ' }}
          />
        </Grid>
        <Grid item style={{ display: 'flex' }}>
          <Button
            variant="contained"
            onClick={submitSignUp}
            disabled={!name || !username || !password || !dogName}
            style={
              username && password
                ? {
                    backgroundColor: palette.paper.secondary,
                    color: palette.text.contrast,
                    width: '100%',
                  }
                : { width: '100%' }
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default SignUpDialog;
